import '@/styles/globals.css'
import { CacheProvider } from '@emotion/react'
import { ThemeProvider } from '@mui/material'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { SessionProvider } from 'next-auth/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AppCustomProps } from '@/types/common'
import type { Session } from 'next-auth'

import { AuthCheck } from '@/AuthCheck'
import { ErrorBoundary } from '@/components/organisms/error/ErrorBoundary'
import { AdminMenu } from '@/components/templates/admin/AdminMenu'
import {
  UnauthenticatedMenu,
  UserMenu,
} from '@/components/templates/user/legacy/UserMenu'
import { DialogsContainer } from '@/components/uiParts/legacy/Dialog'
import { DialogContextProvider } from '@/contexts/Dialog'
import { FormContextProvider } from '@/contexts/Form'
import { LoadingProvider } from '@/contexts/Loading'
import { SnackbarContextProvider } from '@/contexts/Snackbar'
import createEmotionCache from '@/createEmotionCache'
import { RefreshTokenHandler } from '@/RefreshTokenHandler'
import { Snackbar } from '@/Snackbar'
import AdminTheme from '@/styles/admin/theme'
import UserTheme from '@/styles/user/theme'
import { SwrWrapper } from '@/SwrWrapper'
import { initMixpanel, trackPageView } from '@/utils/mixpanelClient'

import '@/utils/i18n'

const clientSideEmotionCache = createEmotionCache()

// TODO:AdminAuthとUserAuthを統一できないか検討する。権限が増えた時に対応できないため。
const App = ({
  Component,
  emotionCache = clientSideEmotionCache,
  pageProps: { session, ...pageProps },
}: AppCustomProps) => {
  const [interval, setInterval] = useState(0)
  const { t } = useTranslation()
  const router = useRouter()

  // Mixpanelの初期化とページビューの追跡
  useEffect(() => {
    initMixpanel() // Initialize Mixpanel

    const handleRouteChange = (url: string) => {
      trackPageView(url) // Track pageviews
    }

    // Track page views on route change
    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  return (
    <CacheProvider value={emotionCache}>
      <SessionProvider session={session as Session} refetchInterval={interval}>
        <LoadingProvider>
          <ErrorBoundary>
            <SnackbarContextProvider>
              <SwrWrapper>
                <DialogContextProvider>
                  <>
                    <Head>
                      {Component.property?.title ? (
                        <title>{Component.property?.title}</title>
                      ) : (
                        Component.property?.titleKey && (
                          <title>
                            Portrich | {t(Component.property.titleKey)}
                          </title>
                        )
                      )}
                    </Head>
                    {
                      // 認証が必要な場合
                      (Component.property?.auth && (
                        <FormContextProvider>
                          {Component.property.viewType === 'admin' ? (
                            <AuthCheck
                              requiredGroups={
                                Component.property.auth.requiredGroups
                              }
                              role={Component.property.auth.role}
                              is_portrich={Component.property.auth.is_portrich}
                            >
                              <ThemeProvider theme={AdminTheme}>
                                <AdminMenu
                                  breadcrumbs={Component.property.breadcrumbs}
                                >
                                  <Component {...pageProps} />
                                </AdminMenu>
                                <DialogsContainer />
                                <Snackbar />
                              </ThemeProvider>
                            </AuthCheck>
                          ) : (
                            <AuthCheck
                              requiredGroups={
                                Component.property.auth.requiredGroups
                              }
                              role={Component.property.auth.role}
                              is_portrich={Component.property.auth.is_portrich}
                            >
                              <ThemeProvider theme={UserTheme}>
                                <UserMenu
                                  breadcrumbs={Component.property.breadcrumbs}
                                >
                                  <Component {...pageProps} />
                                </UserMenu>
                                <DialogsContainer />
                                <Snackbar />
                              </ThemeProvider>
                            </AuthCheck>
                          )}
                        </FormContextProvider>
                      )) || (
                        // 認証が必要ない場合
                        <ThemeProvider theme={UserTheme}>
                          <UnauthenticatedMenu>
                            <Component {...pageProps} />
                            <DialogsContainer />
                            <Snackbar />
                          </UnauthenticatedMenu>
                        </ThemeProvider>
                      )
                    }
                    <RefreshTokenHandler setInterval={setInterval} />
                  </>
                </DialogContextProvider>
              </SwrWrapper>
            </SnackbarContextProvider>
          </ErrorBoundary>
        </LoadingProvider>
      </SessionProvider>
    </CacheProvider>
  )
}

export default App
